import { FIELD_TYPE, NOT_FOUND_INDEX } from 'config'
import { SERVICE_ID } from 'entities/Documents'
import { FIELDS as INVOICE_FIELDS } from 'entities/DocumentsInvoiceBase'
import { FIELDS as ORDER_FORM_FIELDS } from 'entities/getOrderFormFields'
import { ContextType } from 'hooks/GeneralOcrTableRefContextProvider'
import { UseFormGetValues, UseFormReturn } from 'react-hook-form'
import { splitName } from 'utils/highlights'

type StatusType = {
  [key: number]: GeneralOcrState | OcrBuilderState | OrderFormState | OCRInvoiceState
}

export abstract class FocusState {
  constructor(protected useFormContext: UseFormReturn) {}
  abstract setFocusField(name: string, generalOcrTableRef?: ContextType): void
}

const getItemLinesIndex = (category: string, getValues: UseFormGetValues<any>) => {
  const itemLines = getValues('itemLines')
  if (!itemLines) {
    return NOT_FOUND_INDEX
  }
  return itemLines.findIndex((itemLine) => itemLine.id === category)
}

export class GeneralOcrState extends FocusState {
  setFocusField(name: string, generalOcrTableRef): void {
    const isField = name.includes('field')
    if (!name || isField) {
      return
    }
    if (generalOcrTableRef && generalOcrTableRef.current) {
      const cellIndexes = splitName(name)
      return generalOcrTableRef.current[cellIndexes[1]][cellIndexes[2]][cellIndexes[3]].click()
    }
  }
}

export class OcrBuilderState extends FocusState {
  setFocusField(name: string): void {
    if (!name) {
      return
    }
    const title = this.useFormContext.getValues('title')
    if (title.name === name) {
      return this.useFormContext.setFocus('title.text')
    }
    const fields = this.useFormContext.getValues('fields')
    const itemIndex = fields.findIndex((field) => field.name === name)
    const isCheckbox = fields[itemIndex].type === FIELD_TYPE.CHECKBOX
    return this.useFormContext.setFocus(`fields[${itemIndex}].${isCheckbox ? 'checked' : 'text'}`)
  }
}

export class OrderFormState extends FocusState {
  setFocusField(name: string): void {
    const [category, fieldName] = splitName(name)
    const isBase = Object.keys(ORDER_FORM_FIELDS).includes(category)
    if (isBase) {
      return this.useFormContext.setFocus(`${category}.${fieldName}`)
    }
    const itemLinesIndex = getItemLinesIndex(category, this.useFormContext.getValues)
    if (itemLinesIndex !== NOT_FOUND_INDEX) {
      return this.useFormContext.setFocus(`itemLines[${itemLinesIndex}].${fieldName}`)
    }
  }
}

export class OCRInvoiceState extends FocusState {
  setFocusField(name: string): void {
    const [category, fieldName] = splitName(name)
    const isBase = Object.keys(INVOICE_FIELDS).includes(category)
    if (isBase) {
      return this.useFormContext.setFocus(`${category}.${fieldName}`)
    }
    const itemLinesIndex = getItemLinesIndex(category, this.useFormContext.getValues)
    if (itemLinesIndex !== NOT_FOUND_INDEX) {
      return this.useFormContext.setFocus(`itemLines[${itemLinesIndex}].${fieldName}`)
    }
  }
}

export class FocusStateFactory {
  static create(ocrServiceId: number, useFormContext: UseFormReturn): FocusState {
    const statuses: StatusType = {
      [SERVICE_ID.GENERAL_OCR]: new GeneralOcrState(useFormContext),
      [SERVICE_ID.OCR_BUILDER]: new OcrBuilderState(useFormContext),
      [SERVICE_ID.ORDER_FORM]: new OrderFormState(useFormContext),
      [SERVICE_ID.INVOICEV2]: new OCRInvoiceState(useFormContext),
    }
    const status = statuses[ocrServiceId]
    if (!status) throw new Error('unsupported ocrServiceId')
    return status
  }
}
